import { Component, Fragment } from 'react';
import { withTheme } from 'styled-components';
import type { WithThemeProps } from '@simplywallst/ui-core';
import { LinePath } from '@visx/shape';
import { localPoint } from '@visx/event';
import { AxisTop } from '@visx/axis';
import type { D3Scale } from '@visx/scale';
import { min, max } from 'd3-array';
import type { PriceEntity } from '@components/PriceChart/utils/types';
import type { ScaleTime, ScaleLinear } from 'd3-scale';
import {
  xStock,
  yStock,
  getScaleY,
  getScaleX,
} from '@/components/PriceChart/utils/stateUpdate';
import { cn } from '@simplywallst/ui-core/next';

const trackHeight = 40;

const getUpdatedScales = (data, width) => {
  const yMin = min(data, yStock);
  const yMax = max(data, yStock);
  const yScale = getScaleY(trackHeight, yMin || 0, yMax || 0);
  const xScale = getScaleX(data, width);
  return {
    yScale,
    xScale,
  };
};

interface Props {
  dx: number;
  data: PriceEntity[];
  width: number;
  disabled: boolean;
  xScaleWidth: number;
  handleScrollEnd: () => void;
  handleScrollMove: (n: number) => void;
  handleScrollStart: (n: number) => void;
}

interface State {
  data: PriceEntity[];
  xScale: ScaleTime<number, number>;
  yScale: ScaleLinear<number, number>;
  isScrolling: boolean;
  scrollDelta: number;
  scrollStartXDelta: number;
}

class PriceChartPeriod extends Component<Props & WithThemeProps, State> {
  static defaultProps: Props = {
    dx: 0,
    data: [],
    width: 0,
    disabled: false,
    xScaleWidth: 0,
    handleScrollEnd: () => undefined,
    handleScrollMove: () => undefined,
    handleScrollStart: () => undefined,
  };

  constructor(props: Props & WithThemeProps) {
    super(props);
    this.state = {
      data: props.data,
      ...getUpdatedScales(props.data, props.width),
      isScrolling: false,
      scrollDelta: 0,
      scrollStartXDelta: 0,
    };
  }

  componentDidUpdate(prevProps) {
    const { data, width } = this.props;
    const { data: prevData, width: prevWidth } = prevProps;
    if (data !== undefined && prevData !== undefined) {
      let updateState = false;
      if (data.length > 1 && prevData.length > 1) {
        if (
          data[0].close !== prevData[0].close ||
          data[data.length - 1].close !== prevData[prevData.length - 1].close
        ) {
          updateState = true;
        }
      }
      if (data.length !== prevData.length && data.length > 0) {
        updateState = true;
      }
      if (width !== prevWidth) {
        updateState = true;
      }
      //	Set state
      if (updateState) {
        this.setState({
          ...getUpdatedScales(data, width),
        });
      }
    }
  }

  handleScrollEnd = () => {
    const { handleScrollEnd } = this.props;
    this.setState(
      {
        isScrolling: false,
        scrollDelta: 0,
      },
      () => handleScrollEnd()
    );
  };

  handleScrollMove = (e) => {
    const { xScaleWidth, width, handleScrollMove, disabled } = this.props;
    const { isScrolling, scrollStartXDelta } = this.state;

    if (!isScrolling) return;

    const point = localPoint(e);
    const mouseX = point ? point.x : 0;
    let nextDX = xScaleWidth * (mouseX / width) - width / 2;

    if (scrollStartXDelta > 0 && scrollStartXDelta < width) {
      nextDX = xScaleWidth * (mouseX / width) - scrollStartXDelta;
    }

    if (nextDX > xScaleWidth - width) {
      nextDX = xScaleWidth - width;
    } else if (nextDX < 0) {
      nextDX = 0;
    }
    !disabled && handleScrollMove(nextDX);
  };

  handleScrollStart = (e) => {
    const { dx, xScaleWidth, width, handleScrollStart } = this.props;
    const point = localPoint(e);
    const mouseX = point ? point.x : 0;
    const startXNormalised = mouseX / width;
    const startX = xScaleWidth * startXNormalised;
    const startXDelta = startX + dx;
    this.setState(
      {
        isScrolling: true,
        scrollDelta: 0,
        scrollStartXDelta: startXDelta,
      },
      () => handleScrollStart(startXDelta)
    );
  };

  render() {
    const { handleScrollEnd, handleScrollMove, handleScrollStart } = this;
    const { xScale, yScale } = this.state;
    const { dx, data, disabled, xScaleWidth, width, theme } = this.props;

    return (
      <svg
        width={width}
        height={trackHeight}
        xmlns="http://www.w3.org/2000/svg"
        className="block"
      >
        {/* <rect
          x="0"
          y="0"
          fill={theme.color.charcoal}
          width={width}
          height={trackHeight}
        /> */}
        {xScale && yScale && (
          <Fragment>
            <LinePath
              x={(d) => xScale(xStock(d)) as number}
              y={(d) => (d.close ? (yScale(d.close) as number) : 0)}
              data={data}
              stroke={theme.color.darkerGray}
              defined={(d) => d.close !== null}
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <AxisTop
              scale={xScale as D3Scale<number, number>}
              numTicks={5}
              top={trackHeight / 2}
            >
              {({ ticks }) =>
                ticks.map((n, i) => (
                  <g
                    key={`price-chart-track-axis-${i}-${n.formattedValue}`}
                    transform={`translate(${n.to.x}, 0)`}
                  >
                    <text y="4" fontSize="12" textAnchor="middle" fill="white">
                      {n.formattedValue}
                    </text>
                  </g>
                ))
              }
            </AxisTop>
          </Fragment>
        )}
        <rect
          x={(-dx / xScaleWidth) * width}
          y="0"
          fill="var(--trend-color)"
          opacity={0.05}
          width={(width / xScaleWidth) * width}
          height={trackHeight}
          rx="4"
          ry="4"
        />
        <rect
          x={(-dx / xScaleWidth) * width}
          y="0"
          stroke="var(--trend-color)"
          fill="none"
          width={(width / xScaleWidth) * width}
          height={trackHeight}
          rx="4"
          ry="4"
        />
        {!disabled && (
          <rect
            x="0"
            y="0"
            fill="transparent"
            width={width}
            height={trackHeight}
            onMouseUp={handleScrollEnd}
            onTouchEnd={handleScrollEnd}
            onMouseLeave={handleScrollEnd}
            onTouchMove={handleScrollMove}
            onMouseMove={handleScrollMove}
            onMouseDown={handleScrollStart}
            onTouchStart={handleScrollStart}
            className={cn(!disabled && 'cursor-grab active:cursor-grabbing')}
          />
        )}
      </svg>
    );
  }
}

export const PriceChartPeriodTrack = withTheme(PriceChartPeriod);
